import { requestV2 } from '@ltvco/refresh-lib/utils';
import {
  CommunityBuzzBootstrapParams,
  CommunityBuzzBootstrapResponse,
  CommunityBuzzLLMParams,
  CommunityBuzzLLMResponse,
  CreateAnswerParams,
  CreateQuestionParams,
  GetQuestionParams,
  GetQuestionResponse,
  GetQuestionsParams,
  GetQuestionsResponse,
} from './interfaces';

const communityBuzzBaseUrl = '/oram/community-buzz';
const communityBuzzLLMUrl = '/ms/insight/communityBuzz';

const normalizePage = (page?: number) => {
  if (page === null || page === undefined) return 1;
  if (page < 0) return 1;
  return page;
};

const normalizePageSize = (pageSize?: number) => {
  if (!pageSize || pageSize < 1) return String(20);
  if (pageSize > 100) return String(100);
  return String(pageSize);
};

const getCommunityBuzzHeaders = (token?: string) => ({
  'content-type': 'application/json',
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const generateCommunityBuzz = async (
  params: CommunityBuzzLLMParams,
  token?: string,
): Promise<CommunityBuzzLLMResponse> => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return requestV2({
    url: communityBuzzLLMUrl,
    options: init,
  });
};

const bootstrapCommunityBuzzQuestions = async (
  params: CommunityBuzzBootstrapParams,
  token?: string,
): Promise<CommunityBuzzBootstrapResponse> => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return requestV2({
    url: `${communityBuzzBaseUrl}/questions/temp-bootstrap`,
    options: init,
  });
};

const getCommunityBuzzQuestion = async (
  id: number,
  token?: string,
): Promise<GetQuestionResponse> => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'GET',
  };

  return requestV2({
    url: `${communityBuzzBaseUrl}/question/${id}`,
    options: init,
  });
};

const createQuestion = async (params: CreateQuestionParams, token?: string) => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return await requestV2({
    url: `${communityBuzzBaseUrl}/questions`,
    options: init,
  });
};

const createAnswer = async (params: CreateAnswerParams, token?: string) => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return await requestV2({
    url: `${communityBuzzBaseUrl}/question/${params.question_id}/answer`,
    options: init,
  });
};

const getQuestions = async (
  params: GetQuestionsParams,
  token?: string,
): Promise<GetQuestionsResponse> => {
  const searchParams = new URLSearchParams({
    year: params.year,
    make: params.make,
    model: params.model,
    trim: params.trim,
  });

  if (params.page !== undefined && params.page !== null) {
    searchParams.set('page', normalizePage(params.page).toString());
  }
  if (params.page_size !== undefined && params.page_size !== null) {
    searchParams.set('page_size', normalizePageSize(params.page_size));
  }

  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'GET',
  };

  return await requestV2({
    url: `${communityBuzzBaseUrl}/questions?${searchParams.toString()}`,
    options: init,
  });
};

const getQuestion = async (params: GetQuestionParams, token?: string) => {
  const init = {
    headers: getCommunityBuzzHeaders(token),
    method: 'GET',
  };

  return await requestV2({
    url: `${communityBuzzBaseUrl}/question/${params.id}`,
    options: init,
  });
};

export const communityBuzzRequests = {
  generateCommunityBuzz,
  bootstrapCommunityBuzzQuestions,
  getCommunityBuzzQuestion,
  createQuestion,
  createAnswer,
  getQuestions,
  getQuestion,
};
